import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Package, CheckCircle, XCircle, Clock, Loader2, ShieldCheck, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminClientOrdersPage = () => {
  const { getAuthHeaders } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);

  const load = async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await axios.get(`${API}/admin/client-orders${params}`, { headers: getAuthHeaders() });
      setOrders(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  const approveOrder = async (id) => {
    setActionLoading(id);
    try {
      await axios.put(`${API}/admin/client-orders/${id}/approve`, {}, { headers: getAuthHeaders() });
      toast.success('Orden aprobada');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setActionLoading(null); }
  };

  const completeOrder = async (id) => {
    setActionLoading(id);
    try {
      await axios.put(`${API}/admin/client-orders/${id}/complete`, {}, { headers: getAuthHeaders() });
      toast.success('Orden completada');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setActionLoading(null); }
  };

  const rejectOrder = async (id) => {
    if (!window.confirm('Rechazar esta orden? Se reembolsara el monto al wallet del cliente.')) return;
    setActionLoading(id);
    try {
      await axios.put(`${API}/admin/client-orders/${id}/reject`, {}, { headers: getAuthHeaders() });
      toast.success('Orden rechazada y reembolsada');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setActionLoading(null); }
  };

  const cancelOrder = async (id) => {
    if (!window.confirm('Cancelar y reembolsar esta orden?')) return;
    setActionLoading(id);
    try {
      await axios.put(`${API}/admin/client-orders/${id}/cancel`, {}, { headers: getAuthHeaders() });
      toast.success('Orden cancelada y reembolsada');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setActionLoading(null); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  const statusConfig = {
    pending_approval: { icon: ShieldCheck, color: 'text-amber-500', bg: 'bg-amber-50', label: 'Por Aprobar' },
    pending: { icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-50', label: 'Pendiente' },
    in_progress: { icon: ArrowRight, color: 'text-blue-500', bg: 'bg-blue-50', label: 'En Proceso' },
    completed: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50', label: 'Completado' },
    cancelled: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50', label: 'Cancelado' },
    rejected: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50', label: 'Rechazada' },
  };

  const filters = [
    { value: 'all', label: 'Todas' },
    { value: 'pending_approval', label: 'Por Aprobar' },
    { value: 'in_progress', label: 'En Proceso' },
    { value: 'completed', label: 'Completadas' },
    { value: 'rejected', label: 'Rechazadas' },
    { value: 'cancelled', label: 'Canceladas' },
  ];

  return (
    <div className="space-y-6" data-testid="admin-client-orders-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Ordenes de Clientes</h1>
        <p className="text-slate-500">Gestiona las ordenes de servicio (pago por wallet)</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {filters.map((f) => (
          <Button key={f.value} variant={filter === f.value ? 'default' : 'outline'} size="sm" onClick={() => { setFilter(f.value); setLoading(true); }}
            className={filter === f.value ? 'bg-blue-700' : ''} data-testid={`filter-${f.value}`}>
            {f.label}
          </Button>
        ))}
      </div>

      {orders.length === 0 ? (
        <Card className="p-12 text-center border-0 shadow-sm">
          <Package className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No hay ordenes</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const sc = statusConfig[o.status] || statusConfig.pending;
            const Icon = sc.icon;
            return (
              <Card key={o.id} className="p-4 border-0 shadow-sm" data-testid={`admin-order-${o.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`mt-1 ${sc.color}`}><Icon className="w-5 h-5" /></div>
                    <div>
                      <p className="font-semibold text-slate-900">{o.service_type}</p>
                      <p className="text-sm text-slate-600">Cliente: {o.client_name || 'N/A'}</p>
                      {o.details?.notes && <p className="text-sm text-slate-500 mt-1">{o.details.notes}</p>}
                      {o.wallet_deducted && <p className="text-xs text-emerald-600 mt-1">Pagado con Wallet</p>}
                      <p className="text-xs text-slate-400 mt-1">{new Date(o.created_at).toLocaleString('es-ES')}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg">${o.amount?.toFixed(2)}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${sc.bg} ${sc.color}`}>{sc.label}</span>
                    {/* Actions for pending_approval */}
                    {o.status === 'pending_approval' && (
                      <div className="flex gap-1 mt-2">
                        <Button size="sm" onClick={() => approveOrder(o.id)} disabled={actionLoading === o.id} className="bg-green-600 hover:bg-green-700 text-xs" data-testid={`approve-order-${o.id}`}>
                          {actionLoading === o.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle className="w-3 h-3 mr-1" /> Aprobar</>}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => rejectOrder(o.id)} disabled={actionLoading === o.id} className="text-red-600 border-red-200 text-xs" data-testid={`reject-order-${o.id}`}>
                          <XCircle className="w-3 h-3 mr-1" /> Rechazar
                        </Button>
                      </div>
                    )}
                    {/* Actions for in_progress */}
                    {o.status === 'in_progress' && (
                      <div className="flex gap-1 mt-2">
                        <Button size="sm" onClick={() => completeOrder(o.id)} disabled={actionLoading === o.id} className="bg-green-600 hover:bg-green-700 text-xs" data-testid={`complete-order-${o.id}`}>
                          {actionLoading === o.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle className="w-3 h-3 mr-1" /> Completar</>}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => cancelOrder(o.id)} disabled={actionLoading === o.id} className="text-red-600 border-red-200 text-xs" data-testid={`cancel-order-${o.id}`}>
                          <XCircle className="w-3 h-3 mr-1" /> Cancelar
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminClientOrdersPage;
